/* import __COLOCATED_TEMPLATE__ from './postings-search.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { restartableTask, timeout } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import { ADMIN_POSTINGS_CONTROLLER } from 'sh-www/controllers/admin/postings';

import { ASSIGNMENT } from 'sh-www/models/assignment';
import { COMPANY } from 'sh-www/models/company';

const CLASS_NAME = 'SH-c-users-search';

const NULL_OPTION_LABEL = 'Any';

const SORTS = {
  NAME: 'name',
  NAME_MATCH: 'nameMatch',
};

const COLLAPSED_FILTERS = {
  APPLICANT_COUNT: '_advancedFilterApplicantsCount',
  CREATED_AT: '_advancedFilterCreatedAtDate',
  LIVE_AT: '_advancedFilterLiveAtDate',
  DEADLINE: '_advancedFilterDeadline',
};

const FILTERS_DEADLINE_DAYS_REMAINING_DEFAULT = 3;
const FILTERS_DEADLINE_HOURS_REMAINING_DEFAULT = 0;

/**
 * Associate a variable name that must be true with the filters that will be conditionally included
 */
const COLLAPSED_FILTER_ASSOCIATIONS = {
  [COLLAPSED_FILTERS.APPLICANT_COUNT]: [
    ADMIN_POSTINGS_CONTROLLER.FILTERS.APPLICANT_COUNT,
    ADMIN_POSTINGS_CONTROLLER.FILTERS.APPLICANT_COUNT_COMPARATOR,
  ],
  [COLLAPSED_FILTERS.CREATED_AT]: [
    ADMIN_POSTINGS_CONTROLLER.FILTERS.CREATED_AT_DATE,
    ADMIN_POSTINGS_CONTROLLER.FILTERS.CREATED_AT_DATE_COMPARATOR,
  ],
  [COLLAPSED_FILTERS.LIVE_AT]: [
    ADMIN_POSTINGS_CONTROLLER.FILTERS.LIVE_AT_DATE,
    ADMIN_POSTINGS_CONTROLLER.FILTERS.LIVE_AT_DATE_COMPARATOR,
  ],
  [COLLAPSED_FILTERS.DEADLINE]: [
    ADMIN_POSTINGS_CONTROLLER.FILTERS.DEADLINE_DAYS_REMAINING,
    ADMIN_POSTINGS_CONTROLLER.FILTERS.DEADLINE_HOURS_REMAINING,
  ],
};

const COMPARISONS = {
  E: '=',
  L: '<',
  LE: '<=',
  G: '>',
  GE: '>=',
};

const ASSIGNMENT_STATUS_NAME_MAPPING = {
  [ASSIGNMENT.STATUS.LIVE]: 'Active',
  [ASSIGNMENT.STATUS.IN_REVIEW]: 'Needs Approval',
  [ASSIGNMENT.STATUS.DRAFT]: 'Draft',
  [ASSIGNMENT.STATUS.CLOSED]: 'Closed',
};

const ASSIGNMENT_PRIVACY_LEVEL_NAME_MAPPING = {
  [ASSIGNMENT.PRIVACY_LEVEL.PUBLIC]: 'Public',
  [ASSIGNMENT.PRIVACY_LEVEL.PRIVATE]: 'Private',
  [ASSIGNMENT.PRIVACY_LEVEL.NETWORK]: 'Network',
  [ASSIGNMENT.PRIVACY_LEVEL.GROUP]: 'Group',
};

const ASSIGNMENT_BLASTED_NAME_MAPPING = {
  0: 'Not Blasted',
  1: 'Already Blasted',
};

const ASSIGNMENT_COMPARATOR_NAME_MAPPING = {
  [COMPARISONS.L]: 'Less than',
  [COMPARISONS.G]: 'Greater than',
}

const ASSIGNMENT_RELATIVE_TIME_NAME_MAPPING = {
  [COMPARISONS.L]: 'Before',
  [COMPARISONS.G]: 'After',
}

const POSTING_STATUS_OPTIONS = [
  ...Object.keys(ASSIGNMENT_STATUS_NAME_MAPPING).map((key) => ({
    key: ASSIGNMENT_STATUS_NAME_MAPPING[key],
    model: key,
  })),
];

const POSTING_PRIVACY_LEVEL_OPTIONS = [
  {
    key: NULL_OPTION_LABEL,
    model: null,
  },
  ...Object.keys(ASSIGNMENT_PRIVACY_LEVEL_NAME_MAPPING).map((key) => ({
    key: ASSIGNMENT_PRIVACY_LEVEL_NAME_MAPPING[key],
    model: key,
  })),
];

const POSTING_BLASTED_OPTIONS = [
  {
    key: NULL_OPTION_LABEL,
    model: null,
  },
  ...Object.keys(ASSIGNMENT_BLASTED_NAME_MAPPING).map((key) => ({
    key: ASSIGNMENT_BLASTED_NAME_MAPPING[key],
    model: key,
  })),
];

const POSTING_APPLICANT_COUNT_LEVEL_OPTIONS = [
  ...Object.keys(ASSIGNMENT_COMPARATOR_NAME_MAPPING).map((key) => ({
    key: ASSIGNMENT_COMPARATOR_NAME_MAPPING[key],
    model: key,
  })),
];

const POSTING_RELATIVE_TIME_OPTIONS = [
  ...Object.keys(ASSIGNMENT_RELATIVE_TIME_NAME_MAPPING).map((key) => ({
    key: ASSIGNMENT_RELATIVE_TIME_NAME_MAPPING[key],
    model: key,
  })),
];

class AdminPostingsSearch extends Component {
  @service moment;
  @service store;

  /**
   * Local copy of _values so we can work on a scratch copy
   */
  @tracked _values;

  /**
   * Value in the input box to be used for type-ahead searching
   * @type {String}
   */
  @tracked _companySelectorInput = null;

  @tracked _advancedFilterApplicantsCount = false;

  @tracked _advancedFilterCreatedAtDate = false;

  @tracked _advancedFilterLiveAtDate = false;

  @tracked _advancedFilterDeadline = false;

  @tracked _availableCompanies = [];

  dateToday = this.moment.moment().toDate();

  constructor() {
    super(...arguments);

    this._values = this.args.values;
    this.openAdvancedFilters(this.args.values);
  }

  get className() {
    return CLASS_NAME;
  }

  get filters() {
    return ADMIN_POSTINGS_CONTROLLER.FILTERS;
  }

  get collapsedFilters() {
    return COLLAPSED_FILTERS;
  }

  get postingStatusOptions() {
    return POSTING_STATUS_OPTIONS;
  }

  get postingPrivacyLevelOptions() {
    return POSTING_PRIVACY_LEVEL_OPTIONS;
  }

  get postingBlastedOptions() {
    return POSTING_BLASTED_OPTIONS;
  }

  get postingApplicantCountLevelOptions() {
    return POSTING_APPLICANT_COUNT_LEVEL_OPTIONS;
  }

  get postingRelativeTimeOptions() {
    return POSTING_RELATIVE_TIME_OPTIONS;
  }

  get selectedCompanyLabel() {
    const companySelectorInput = this._companySelectorInput;

    // If we have entered a value, return that
    if (companySelectorInput !== null) {
      return companySelectorInput;
    }

    // Otherwise show the selected company name if any
    const selectedId = this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.COMPANY];
    const company = this._availableCompanies.findBy('id', selectedId);

    return company?.name ?? NULL_OPTION_LABEL;
  }

  get selectedStatusLabel() {
    const statusFilter = this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.STATUS];
    return statusFilter ? ASSIGNMENT_STATUS_NAME_MAPPING[statusFilter] : ASSIGNMENT_STATUS_NAME_MAPPING[ASSIGNMENT.STATUS.LIVE];
  }

  get selectedPrivacyLevelLabel() {
    const privacyLevelFilter = this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.PRIVACY_LEVEL];
    return privacyLevelFilter ? ASSIGNMENT_PRIVACY_LEVEL_NAME_MAPPING[privacyLevelFilter] : NULL_OPTION_LABEL;
  }

  get selectedBlastedLabel() {
    const blastedFilter = this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.BLASTED];
    return blastedFilter ? ASSIGNMENT_BLASTED_NAME_MAPPING[blastedFilter] : NULL_OPTION_LABEL;
  }

  get selectedApplicantCountLabel() {
    const applicantCountFilter = this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.APPLICANT_COUNT_COMPARATOR];
    return applicantCountFilter ? ASSIGNMENT_COMPARATOR_NAME_MAPPING[applicantCountFilter] : ASSIGNMENT_COMPARATOR_NAME_MAPPING[COMPARISONS.L];
  }

  get selectedCreatedAtDateCountLabel() {
    const createdAtDateFilter = this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.CREATED_AT_DATE_COMPARATOR];
    return createdAtDateFilter ? ASSIGNMENT_RELATIVE_TIME_NAME_MAPPING[createdAtDateFilter] : ASSIGNMENT_RELATIVE_TIME_NAME_MAPPING[COMPARISONS.L];
  }

  get selectedLiveAtDateCountLabel() {
    const liveAtDateFilter = this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.LIVE_AT_DATE_COMPARATOR];
    return liveAtDateFilter ? ASSIGNMENT_RELATIVE_TIME_NAME_MAPPING[liveAtDateFilter] : ASSIGNMENT_RELATIVE_TIME_NAME_MAPPING[COMPARISONS.L];
  }

  get deadlineDaysValue() {
    return this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.DEADLINE_DAYS_REMAINING] ?? FILTERS_DEADLINE_DAYS_REMAINING_DEFAULT;
  }

  get deadlineHoursValue() {
    return this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.DEADLINE_HOURS_REMAINING] ?? FILTERS_DEADLINE_HOURS_REMAINING_DEFAULT;
  }

  /** @type {A<Object>} */
  get companyOptions() {
    const availableCompanies = this._availableCompanies || [];

    return [
      {
        key: NULL_OPTION_LABEL,
        model: null,
      },
      ...availableCompanies.map((company) => ({
        key: `${company.name} (${company.memberCount} users)`,
        model: company.id,
      })),
    ];
  }

  /** @type {A<Object>} */
  get sortOptions() {
    const sortValue = this.sortValue;

    return Object.entries(ADMIN_POSTINGS_CONTROLLER.SORT_NAMES).map(([value, title]) => ({
      title,
      value,
      selected: value === sortValue,
    }));
  }

  @restartableTask
  *loadCompanies(immediate = false) {
    if (!immediate) {
      yield timeout(250);
    }

    const selectedCompanyId = this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.COMPANY];

    // If we have a selected ID, load just that one
    if (selectedCompanyId) {
      const loadedCompany = this._availableCompanies.findBy('id', selectedCompanyId);
      if (loadedCompany) {
        // Already loaded
        return;
      }

      const company = yield this.store.findRecord('company', selectedCompanyId);
      this._availableCompanies = [company];
      return;
    }

    // We need to load multiple companies
    const params = {
      page: 1,
      count: 30,
      sort: SORTS.NAME,
      filter: {
        notType: COMPANY.TYPES.SHELL,
      },
    };

    if (this._companySelectorInput) {
      params.filter = {
        ...params.filter,
        name: this._companySelectorInput,
      };
      params.sort = SORTS.NAME_MATCH;
    }

    this._availableCompanies = yield this.store.query('company', params);
  }

  /**
   * These filters contain default values, so only open by default if the param is in the URL
   *
   * @param {Object} filter query key value pairs
   */
  @action
  openAdvancedFilters(currentFilters) {
    // number of applicants
    if (
      currentFilters.applicant_count !== undefined
      && currentFilters.applicant_count_comparator !== undefined
    ) {
      this._advancedFilterApplicantsCount = true;
    }

    // created at date
    if (
      currentFilters.created_at !== undefined
      && currentFilters.created_at_comparator !== undefined
    ) {
      this._advancedFilterCreatedAtDate = true;
    }

    // live at date
    if (
      currentFilters.live_at !== undefined
      && currentFilters.live_at_comparator !== undefined
    ) {
      this._advancedFilterLiveAtDate = true;
    }
  }

  @action
  onSelectInput(filter, value) {
    switch (filter) {
      case ADMIN_POSTINGS_CONTROLLER.FILTERS.COMPANY:
        this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.COMPANY] = null;
        this._companySelectorInput = value;
        this.loadCompanies.perform();
        break;
    }
  }

  /**
   * Toggling a filter will remove it instead of setting to false
   *
   * @param {String} filter
   */
  @action
  toggleFilter(filter) {
    const value = this._values[filter];
    this._values = {
      ...this._values,
      [filter]: value ? undefined : !value,
    };
  }

  @action
  setFilter(filter, value) {
    const valueOrNull = value === '' ? null : value;

    switch (filter) {
      case ADMIN_POSTINGS_CONTROLLER.FILTERS.COMPANY:
        this._companySelectorInput = null; // Clear the input
        break;
    }

    this._values = {
      ...this._values,
      [filter]: valueOrNull,
    };
  }

  @action
  setDateFilter(filter, date) {
    const dateOrNull = !date ? null : this.moment.moment(date).format('MM/DD/YYYY');

    // prevent removing required input
    this._values = {
      ...this._values,
      [filter]: dateOrNull,
    };
  }

  @action
  setDefaultDateIfNone(filter) {
    // force filter to have a value when filter is enabled
    if (!this._values[filter]) {
      this._values = {
        ...this._values,
        [filter]: this.moment.moment().format('MM/DD/YYYY'),
      };
    }
  }

  @action
  toggleAdvancedFilter(filter) {
    // set defaults upon opening collapsed filter
    if (!this[filter]) {
      switch (filter) {
        case COLLAPSED_FILTERS.APPLICANT_COUNT:
          if (!this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.APPLICANT_COUNT]) {
            this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.APPLICANT_COUNT] = 3;
            this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.APPLICANT_COUNT_COMPARATOR] = COMPARISONS.L;
          }

          break;

        case COLLAPSED_FILTERS.CREATED_AT:
          if (!this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.CREATED_AT_DATE]) {
            this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.CREATED_AT_DATE] = this.moment.moment().format('MM/DD/YYYY');
            this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.CREATED_AT_DATE_COMPARATOR] = COMPARISONS.L;
          }

          break;

        case COLLAPSED_FILTERS.LIVE_AT:
          if (!this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.LIVE_AT_DATE]) {
            this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.LIVE_AT_DATE] = this.moment.moment().format('MM/DD/YYYY');
            this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.LIVE_AT_DATE_COMPARATOR] = COMPARISONS.L;
          }

          break;

        case COLLAPSED_FILTERS.DEADLINE:
          if (
              !this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.DEADLINE_DAYS_REMAINING]
              || !this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.DEADLINE_HOURS_REMAINING]
            ) {
            this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.DEADLINE_DAYS_REMAINING] = FILTERS_DEADLINE_DAYS_REMAINING_DEFAULT;
            this._values[ADMIN_POSTINGS_CONTROLLER.FILTERS.DEADLINE_HOURS_REMAINING] = FILTERS_DEADLINE_HOURS_REMAINING_DEFAULT;
          }

          break;
      }
    }

    this[filter] = !this[filter];
  }

  @action
  handleReset() {
    this._values = {};

    // collapse advanced filters
    Object.values(COLLAPSED_FILTERS).forEach((collapseState) => {
      this[collapseState] = false;
    });

    this.args.onReset();
  }

  @action
  handleSearch(e) {
    e.preventDefault();

    // exclude collapsed values if their group is not checked
    Object.keys(COLLAPSED_FILTER_ASSOCIATIONS).forEach((collapseState) => {
      // if unchecked
      if (!this[collapseState]) {
        COLLAPSED_FILTER_ASSOCIATIONS[collapseState].forEach((filter) => {
          this._values[filter] = undefined;
        });
      }
    });

    this.args.setValues(this._values);
  }
}

export default AdminPostingsSearch;
